import { AlertCaseDto } from 'src/app/models/alert';
import { CaseUserStatus } from 'src/app/models/referenceData';
import { findShopGroup } from 'src/app/shared/handler/common-functions.util';
import { Lamp, lampStates } from './alert-lamps';

export const HISTORY_DAYS = 30;
export const HISTORY_LIMIT = 50;

const DAY_MS = 86_400_000;

export interface HistoryRow {
  id: string;
  firedAtUtc: string;
  severity: string;
  case: RowCase | null;
  lamps: Lamp[];
  shopName: string | null;
}

export interface CaseRef {
  id: string | null;
  orderNumber: string | null;
}

export interface RowCase extends CaseRef {
  userStatusId: number;
  statusSetUtc: string | null;
}

export type CaseRelation =
  | { kind: 'none' }
  | { kind: 'same'; orderNumber: string | null }
  | { kind: 'other-open'; orderNumber: string | null; statusKey: string }
  | { kind: 'other-closed'; orderNumber: string | null; closedUtc: string | null };

export function toCaseRef(id: string | undefined, orderNumber: string | undefined): CaseRef {
  return { id: id ?? null, orderNumber: orderNumber ?? null };
}

export function toRowCase(alertCase: AlertCaseDto | undefined): RowCase | null {
  if (!alertCase) return null;
  const set = alertCase.status?.createDateTimeUtc;
  return {
    ...toCaseRef(alertCase.caseId, alertCase.orderNumber),
    userStatusId: alertCase.status?.userStatusId ?? 0,
    statusSetUtc: set && Date.parse(set) > 0 ? new Date(set).toISOString() : null,
  };
}

function sameCase(a: CaseRef, b: CaseRef): boolean {
  if (a.id && b.id) return a.id === b.id;
  return !!a.orderNumber && a.orderNumber === b.orderNumber;
}

// A terminal status's set time is when the case closed.
export function caseRelation(rowCase: RowCase | null, openCase: CaseRef | null, statuses: CaseUserStatus[]): CaseRelation {
  if (!rowCase) return { kind: 'none' };
  const { orderNumber } = rowCase;
  if (openCase && sameCase(rowCase, openCase)) return { kind: 'same', orderNumber };
  const status = statuses.find(s => s.id === rowCase.userStatusId);
  if (status?.isTerminal) return { kind: 'other-closed', orderNumber, closedUtc: rowCase.statusSetUtc };
  return { kind: 'other-open', orderNumber, statusKey: status?.name ?? '@notStarted' };
}

export function toHistoryRow(alert: any): HistoryRow {
  return {
    id: alert.id,
    lamps: lampStates(alert.vehicleState?.dashboard),
    firedAtUtc: alert.createDateTimeUtc ? new Date(alert.createDateTimeUtc).toISOString() : '',
    severity: alert.severity?.name ?? alert.severityName ?? '',
    case: toRowCase(alert.cases?.[0]),
    shopName: findShopGroup(alert.groups)?.name ?? null,
  };
}

export function sparklineBuckets(rows: HistoryRow[], days = HISTORY_DAYS, now = Date.now()): number[] {
  const buckets = new Array<number>(days).fill(0);
  for (const r of rows) {
    const age = Math.max(0, Math.floor((now - Date.parse(r.firedAtUtc)) / DAY_MS));
    if (age < days) buckets[days - 1 - age]++;
  }
  return buckets;
}

export function sparklineRead(buckets: number[]): { count: number; days: number } | null {
  const first = buckets.findIndex(b => b > 0);
  if (first < 0) return null;
  return { count: buckets.reduce((sum, b) => sum + b, 0), days: buckets.length - first };
}

export function isTruncated(rows: unknown[], limit = HISTORY_LIMIT): boolean {
  return rows.length >= limit;
}

export function barHeightPx(count: number, max: number): number {
  return count > 0 && max > 0 ? 10 + Math.round(18 * count / max) : 4;
}
